import classNames from 'classnames/bind';
import useOffClick from '@flight-workspace/common-client/src/hooks/useOffClick';
import { SearchType } from 'contexts/searchParams';
import useFilters, { FilterBy, IFilters } from 'hooks/useDomesticFilters';
import {
  Checkbox,
  CheckboxGroup,
  DepartureTimeFilter,
  Footer,
  Header,
  Section,
  Tab,
  TabContent,
  TabPane,
} from 'list/common/filter';
import { Shape } from 'list/common/filter/Checkbox';
import PriceRangeFilter, { PriceUnit } from 'list/common/filter/PriceRange';
import styles from './index.module.scss';
import { FilterTab, SeatClass } from './types';
import { IFilterOptions } from './utils';

const cx = classNames.bind(styles);

interface IProps {
  activeTab: FilterTab;
  filters: IFilters;
  isOneWay: boolean;
  options: IFilterOptions;
  whetherToSave: boolean;
  onClose: () => void;
  setActiveTab: (tabId: FilterTab) => void;
  setFilters: (filters: IFilters) => void;
  toggleToSave: () => void;
}

const clickCodeMap = new Map([
  [SeatClass.SPECIAL_DEAL, 'lft.special'],
  [SeatClass.DISCOUNT, 'lft.discount'],
  [SeatClass.ECONOMY, 'lft.economy'],
  [SeatClass.BUSINESS, 'lft.business'],
]);

const seatClassMap = new Map([
  [SeatClass.SPECIAL_DEAL, '특가석'],
  [SeatClass.DISCOUNT, '할인석'],
  [SeatClass.ECONOMY, '일반석'],
  [SeatClass.BUSINESS, '비즈니스석'],
]);

const tabs: [FilterTab, string][] = [
  [FilterTab.SEAT_CLASS, '좌석등급'],
  [FilterTab.AIRLINE, '항공사'],
  [FilterTab.TIME_AND_PRICE, '시각/가격'],
];

export const nPayAvailableAirlines = new Set(['7C', 'BX', 'LJ', 'OZ', 'TW']);

function convertMapToOptions(map: Map<any, string>) {
  return Array.from(map).map(([value, label]) => ({
    label,
    value,
    clickCode: clickCodeMap.get(value),
  }));
}

export default function Filter({
  activeTab,
  filters,
  isOneWay,
  options: { airlines, prices },
  whetherToSave,
  onClose,
  setActiveTab,
  setFilters,
  toggleToSave,
}: IProps) {
  const {
    seatClassFilters,
    resetSeatClassFilters,
    toggleSeatClassFilter,
    airlineFilters,
    resetAirlineFilters,
    toggleAirlineFilter,
    handleAirlineMultiSelect,
    timeFiltersForDeparture,
    resetTimeFiltersForDeparture,
    toggleTimeFiltersForDeparture,
    timeFiltersForArrival,
    resetTimeFiltersForArrival,
    toggleTimeFiltersForArrival,
    priceRange,
    setPriceRange,
    filters: newFilters,
  } = useFilters({
    ...filters,
    [FilterBy.AIRLINE]:
      filters[FilterBy.AIRLINE].size === 0
        ? new Set(
            convertMapToOptions(airlines)
              .filter(({ label }) => label)
              .map(({ value }) => value),
          )
        : filters[FilterBy.AIRLINE],
  });

  const ref = useOffClick(onClose);

  const airlineFilterOptions = convertMapToOptions(airlines)
    .filter(({ label }) => label)
    .sort((a, b) => a.label.localeCompare(b.label, 'ko-KR'))
    .map(filter => ({
      ...filter,
      label: (
        <>
          {filter.label}
          {nPayAvailableAirlines.has(filter.value) && (
            <i className={cx('label_npay')}>왕복 시 1%적립</i>
          )}
        </>
      ),
    }));

  function onConfirm() {
    setFilters(newFilters);
    onClose();
  }

  function onReset() {
    resetSeatClassFilters();
    resetAirlineFilters();
    resetTimeFiltersForDeparture();
    resetTimeFiltersForArrival();
    setPriceRange(undefined);
  }

  return (
    <div className={cx('Filter')} role="dialog" ref={ref}>
      <div className={cx('content')}>
        <Header
          searchType={SearchType.DOMESTIC}
          onClose={onClose}
          onReset={onReset}
        />
        <div className={cx('tabs')}>
          {tabs.map(([id, name]) => (
            <Tab
              key={id}
              activeTab={activeTab}
              clickCode={`lft.${id}`}
              id={id}
              onClick={setActiveTab}
            >
              {name}
            </Tab>
          ))}
        </div>
        <TabContent activeTab={activeTab}>
          <TabPane tabId={FilterTab.SEAT_CLASS}>
            <Section title="좌석등급">
              <div className={cx('radiogroup')} role="radiogroup">
                <Checkbox
                  clickCode="lft.seatall"
                  block
                  isChecked={seatClassFilters.size === 0}
                  shape={Shape.ROUND}
                  onChange={resetSeatClassFilters}
                >
                  전체
                </Checkbox>
                <CheckboxGroup
                  checked={seatClassFilters}
                  options={convertMapToOptions(seatClassMap)}
                  shape={Shape.ROUND}
                  onChange={toggleSeatClassFilter}
                />
              </div>
            </Section>
          </TabPane>
          <TabPane tabId={FilterTab.AIRLINE}>
            <Section title="항공사">
              <div className={cx('checkboxes')}>
                <div className={cx('sub')} role="row">
                  <button
                    type="button"
                    role="checkbox"
                    className={cx('toggle_all')}
                    data-event-nlog="click"
                    data-event-area-code="lft.alall"
                    disabled={
                      newFilters[FilterBy.AIRLINE].size ===
                      airlineFilterOptions.length
                    }
                    onClick={() =>
                      handleAirlineMultiSelect(
                        airlineFilterOptions.map(({ value }) => value),
                      )
                    }
                  >
                    전체 선택
                  </button>
                  <button
                    type="button"
                    role="checkbox"
                    className={cx('toggle_all')}
                    data-event-nlog="click"
                    data-event-area-code="lft.alallcancel"
                    disabled={newFilters[FilterBy.AIRLINE].size === 0}
                    onClick={resetAirlineFilters}
                  >
                    전체 지우기
                  </button>
                </div>
                <CheckboxGroup
                  checked={airlineFilters}
                  commonClickCode="lft.alsel"
                  options={airlineFilterOptions}
                  shape={Shape.SQUARE}
                  onChange={toggleAirlineFilter}
                />
              </div>
            </Section>
          </TabPane>
          <TabPane tabId={FilterTab.TIME_AND_PRICE}>
            <Section title="출발시각">
              <DepartureTimeFilter
                areaCode="lft"
                selected={timeFiltersForDeparture}
                seq="1"
                title="가는 날"
                onChange={toggleTimeFiltersForDeparture}
                onSelectAll={resetTimeFiltersForDeparture}
              />
              {!isOneWay && (
                <DepartureTimeFilter
                  areaCode="lft"
                  selected={timeFiltersForArrival}
                  seq="2"
                  title="오는 날"
                  onChange={toggleTimeFiltersForArrival}
                  onSelectAll={resetTimeFiltersForArrival}
                />
              )}
            </Section>
            <Section title="가격대">
              <PriceRangeFilter
                areaCode="lft"
                min={prices.min}
                max={prices.max}
                selectedRange={priceRange}
                unit={PriceUnit.TENTHOUSAND}
                onAfterChange={([changedMin, changedMax]: number[]) =>
                  setPriceRange({
                    min: changedMin > prices.min ? changedMin : undefined,
                    max: changedMax < prices.max ? changedMax : undefined,
                  })
                }
              />
            </Section>
          </TabPane>
        </TabContent>
        <Footer
          searchType={SearchType.DOMESTIC}
          isChecked={whetherToSave}
          tooltip="선택하신 국내선 검색 조건을 저장합니다."
          onCancel={onClose}
          onConfirm={onConfirm}
          onToggle={toggleToSave}
          disabledSubmit={newFilters[FilterBy.AIRLINE].size === 0}
        />
      </div>
    </div>
  );
}
